import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertCircle, RefreshCw, ExternalLink, Smartphone, Trash2, Sparkles } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [status, setStatus] = useState(null);
  const [resyncing, setResyncing] = useState(false);
  const [purging, setPurging] = useState(false);

  const load = async () => {
    const { data } = await api.get("/integrations/status");
    setStatus(data);
  };
  useEffect(() => { load(); }, []);

  const resync = async () => {
    if (!window.confirm("Wipe local orders and re-pull everything from Base44? Paint progress will be preserved.")) return;
    setResyncing(true);
    try {
      const { data } = await api.post("/orders/resync");
      toast.success(`Resynced ${data.synced} orders from Base44`);
      await load();
    } catch (e) {
      toast.error("Resync failed");
    } finally {
      setResyncing(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1100px] animate-fade-in">
      <div className="mb-8">
        <div className="label-eyebrow">Settings</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">
          Integrations
        </h1>
        <p className="text-muted-foreground mt-1">
          Live connections to your data sources.
        </p>
      </div>

      <div className="space-y-4">
        {status && (
          <div className="surface-card p-5" data-testid="integration-base44">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-heading font-semibold text-lg">Base44 — Ordering API</h3>
                  {status.base44.connected ? (
                    <span className="pill bg-primary/10 text-primary border-primary/30">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Connected
                    </span>
                  ) : (
                    <span className="pill bg-[#E5A93D]/15 text-[#7c5a14] border-[#E5A93D]/40">
                      <AlertCircle className="w-3.5 h-3.5" /> Not configured
                    </span>
                  )}
                </div>
                <div className="mt-3 text-sm space-y-1">
                  {status.base44.app_id && (
                    <div>
                      <span className="text-muted-foreground">App ID:</span>{" "}
                      <span className="font-mono">{status.base44.app_id}</span>
                    </div>
                  )}
                  {status.base44.last_synced_at && (
                    <div className="text-muted-foreground">
                      Last sync: {fmtDate(status.base44.last_synced_at)} ·{" "}
                      <span className="font-medium text-foreground">
                        {status.base44.last_synced_count} order{status.base44.last_synced_count === 1 ? "" : "s"}
                      </span>
                    </div>
                  )}
                  {!status.base44.connected && (
                    <div className="text-sm text-muted-foreground">{status.base44.note}</div>
                  )}
                </div>
              </div>
              {status.base44.connected && (
                <Button
                  variant="outline"
                  onClick={resync}
                  disabled={resyncing}
                  data-testid="resync-base44"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${resyncing ? "animate-spin" : ""}`} />
                  Resync all
                </Button>
              )}
            </div>
          </div>
        )}

        {status && (
          <div className="surface-card p-5" data-testid="integration-stripe">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-heading font-semibold text-lg">Stripe — Payments</h3>
                  {status.stripe.connected ? (
                    <span className="pill bg-primary/10 text-primary border-primary/30">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Connected
                    </span>
                  ) : (
                    <span className="pill bg-[#E5A93D]/15 text-[#7c5a14] border-[#E5A93D]/40">
                      <AlertCircle className="w-3.5 h-3.5" /> Mocked
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  Provide your Stripe Restricted Key (rk_live_…) with <span className="font-mono">Read PaymentIntents</span> permission to attach real payment details to each order.
                </p>
                <div className="text-xs text-muted-foreground mt-2 italic">{status.stripe.note}</div>
              </div>
              <button className="text-sm text-primary hover:underline inline-flex items-center gap-1 shrink-0">
                Configure <ExternalLink className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="surface-card p-6 mt-8" data-testid="tutorial-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-primary mt-1" />
            <div>
              <div className="label-eyebrow">Onboarding</div>
              <h2 className="font-heading text-xl font-semibold mt-1">Product tour</h2>
              <p className="text-sm text-muted-foreground mt-2 max-w-xl">
                Walk through every feature of the console — Dashboard, Orders, Paint, Deliveries, Stripe reconciliation and more. About 2 minutes.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            data-testid="replay-tutorial-btn"
            onClick={() => window.dispatchEvent(new CustomEvent("cd:replay-tutorial"))}
          >
            <Sparkles className="w-4 h-4 mr-2" /> Replay tutorial
          </Button>
        </div>
      </div>

      <div className="surface-card p-6 mt-6">
        <div className="flex items-start gap-3">
          <Smartphone className="w-5 h-5 text-primary mt-1" />
          <div>
            <div className="label-eyebrow">Mobile access</div>
            <h2 className="font-heading text-xl font-semibold mt-1">Installable on phone &amp; tablet</h2>
            <p className="text-sm text-muted-foreground mt-2">
              The console is fully responsive — bookmark it on your iOS or Android home screen for instant access from the warehouse, paint shop, or delivery route. Native iOS/Android apps can follow in a later iteration.
            </p>
          </div>
        </div>
      </div>

      {isAdmin && (
        <div className="surface-card p-6 mt-6 border border-destructive/20" data-testid="danger-zone">
          <div className="flex items-start gap-3">
            <Trash2 className="w-5 h-5 text-destructive mt-1" />
            <div className="flex-1">
              <div className="label-eyebrow">Danger zone · Admin only</div>
              <h2 className="font-heading text-xl font-semibold mt-1">Purge test data</h2>
              <p className="text-sm text-muted-foreground mt-2 mb-4">
                Automated test runs sometimes leave behind sample manifests, deliveries and expenses. Use these buttons to clean them out. Orders (from Base44), products, and your admin account are never touched.
              </p>
              <div className="flex flex-wrap gap-3">
                <PurgeButton
                  label="Purge test-flagged data"
                  variant="outline"
                  testid="purge-test-btn"
                  confirmTitle="Purge test-flagged data?"
                  confirmBody="Deletes deliveries, manifests, expenses, and non-admin users whose fields contain 'TEST_', 'csrf-test', or similar test-agent markers. Safe on real data."
                  endpoint="/admin/purge-test-data"
                  busy={purging}
                  setBusy={setPurging}
                  formatResult={(d) => `${d.deliveries_deleted} deliveries, ${d.manifests_deleted} manifests, ${d.expenses_deleted} expenses, ${d.users_deleted} users, ${d.products_deleted ?? 0} products`}
                />
                <PurgeButton
                  label="Wipe ALL deliveries & manifests"
                  variant="destructive"
                  testid="purge-all-btn"
                  confirmTitle="Wipe ALL deliveries + manifests?"
                  confirmBody="Removes EVERY delivery and manifest — no exceptions. Use this once to reset both to empty, then start using them for real. Orders and products are untouched."
                  endpoint="/admin/purge-all-deliveries-and-manifests"
                  busy={purging}
                  setBusy={setPurging}
                  formatResult={(d) => `${d.deliveries_deleted} deliveries and ${d.manifests_deleted} manifests removed`}
                />
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function PurgeButton({ label, variant, testid, confirmTitle, confirmBody, endpoint, busy, setBusy, formatResult }) {
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(endpoint);
      toast.success(`Cleaned — ${formatResult(data)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant={variant} disabled={busy} data-testid={testid}>
          <Trash2 className="w-4 h-4 mr-2" />
          {label}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{confirmTitle}</AlertDialogTitle>
          <AlertDialogDescription>{confirmBody}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={run}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            data-testid={`${testid}-confirm`}
          >
            Yes, purge
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
